// SPDX-License-Identifier: MIT
import { t } from '../i18n/t';
import { escapeHtml } from '../lib/escape';
import { renderDropZone, type DropZoneFile } from './drop-zone';
import { getAttachmentBridge } from '../platform';
import { removeFromContext, type AttachmentRow } from '../projects/attachments';

export interface ProjectAttachmentsOptions {
  userId: string;
  projectId: string;
  // Fires after each successful upload / delete so the parent card can
  // show or hide the "↻ Régénérer enrichi" button without re-mounting.
  onContextChange?: () => void;
}

// Renders the attachments section under a saved project's preview card.
// Wraps the existing renderDropZone (file pick / drag-drop / paste UX)
// plus a list of already-uploaded files with delete + download actions.
//
// Fetches the initial list async on mount ; subsequent uploads and deletes
// keep the local cache in sync so the user sees their action reflected
// immediately without a network round-trip.
export function renderProjectAttachments(opts: ProjectAttachmentsOptions): HTMLElement {
  const section = document.createElement('section');
  section.className = 'project-attachments';
  section.setAttribute('data-component', 'project-attachments');
  section.innerHTML = `
    <div class="project-attachments__head">
      <h4 class="project-attachments__heading">${escapeHtml(t('attachments.heading'))}</h4>
      <span class="project-attachments__hint">${escapeHtml(t('attachments.hint'))}</span>
    </div>
    <div data-component="project-attachments-dropzone-host"></div>
    <ul class="project-attachments__list" data-component="project-attachments-list" hidden></ul>
  `;

  const dropHost = section.querySelector<HTMLElement>('[data-component="project-attachments-dropzone-host"]')!;
  const list = section.querySelector<HTMLUListElement>('[data-component="project-attachments-list"]')!;

  // Platform attachment store : web → Supabase Storage ; desktop → local
  // in-memory list (no binary, text already piped to the context). The UI below
  // never branches on platform — only on the bridge's capabilities.
  const store = getAttachmentBridge();

  // Local cache : the rendered list mirrors this array, single source of truth.
  let attachments: AttachmentRow[] = [];

  const rerenderList = (): void => {
    list.innerHTML = '';
    if (attachments.length === 0) {
      list.hidden = true;
      return;
    }
    list.hidden = false;
    for (const a of attachments) list.appendChild(buildListItem(a, onDelete, onDownload, store.supportsOpen));
  };

  const onDelete = async (a: AttachmentRow, btn: HTMLButtonElement) => {
    if (!confirm(t('attachments.confirmDelete').replace('{name}', a.name))) return;
    btn.disabled = true;
    btn.textContent = t('attachments.deleting');
    try {
      await store.remove(a.path);
      attachments = attachments.filter((x) => x.path !== a.path);
      rerenderList();
      removeFromContext(opts.userId, opts.projectId, a.name);
      opts.onContextChange?.();
    } catch (err) {
      btn.disabled = false;
      btn.textContent = t('attachments.delete');
      console.warn('[attachments] delete failed:', err);
      alert(t('attachments.deleteError'));
    }
  };

  const onDownload = async (a: AttachmentRow, btn: HTMLButtonElement) => {
    btn.disabled = true;
    const originalLabel = btn.textContent;
    btn.textContent = t('attachments.opening');
    try {
      const url = await store.signedUrl(a.path);
      if (!url) return; // platform keeps no binary (desktop) — nothing to open
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.warn('[attachments] signed URL failed:', err);
      alert(t('attachments.downloadError'));
    } finally {
      btn.disabled = false;
      btn.textContent = originalLabel;
    }
  };

  // Drop-zone instance — renderDropZone validates each file (type + size)
  // and fires onAdd PER accepted file. We pipe each one into uploadAttachment
  // and append the resulting AttachmentRow to the local cache on success.
  // The drop-zone's own card display shows the pending state ; our list shows
  // the persisted state.
  const dropZone = renderDropZone({
    onAdd: (entry) => { void handleUpload(entry); },
  });
  dropHost.appendChild(dropZone);

  async function handleUpload(f: DropZoneFile): Promise<void> {
    try {
      // The bridge owns "ingest this file": persist it (Storage / local) AND
      // extract its text into the context. We just reflect the result + fire
      // the change callback unconditionally so the "Régénérer enrichi" CTA
      // appears for every successful upload, even a binary (text stub → null).
      const row = await store.upload(opts.userId, opts.projectId, f.file);
      attachments = [row, ...attachments];
      rerenderList();
      opts.onContextChange?.();
    } catch (err) {
      console.warn('[attachments] upload failed:', err);
    }
  }

  // Initial load — async, non-blocking.
  store.list(opts.userId, opts.projectId)
    .then((rows) => { attachments = rows; rerenderList(); })
    .catch((err) => { console.warn('[attachments] list failed:', err); });

  return section;
}

function buildListItem(
  a: AttachmentRow,
  onDelete: (a: AttachmentRow, btn: HTMLButtonElement) => void,
  onDownload: (a: AttachmentRow, btn: HTMLButtonElement) => void,
  supportsOpen: boolean,
): HTMLLIElement {
  const li = document.createElement('li');
  li.className = 'project-attachments__item';
  li.setAttribute('data-path', a.path);
  // The open action only renders when the store keeps a fetchable binary
  // (web/Supabase). Desktop keeps no binary, so there is nothing to open.
  const openBtnHtml = supportsOpen
    ? `<button type="button" class="project-attachments__action" data-component="attachment-open">${escapeHtml(t('attachments.open'))}</button>`
    : '';
  li.innerHTML = `
    <div class="project-attachments__file-info">
      <span class="project-attachments__file-icon" aria-hidden="true">${iconFor(a.mime)}</span>
      <div class="project-attachments__file-meta">
        <span class="project-attachments__file-name" title="${escapeHtml(a.name)}">${escapeHtml(a.name)}</span>
        <span class="project-attachments__file-sub">${escapeHtml(formatBytes(a.size))} · ${escapeHtml(formatDate(a.created_at))}</span>
      </div>
    </div>
    <div class="project-attachments__file-actions">
      ${openBtnHtml}
      <button type="button" class="project-attachments__action project-attachments__action--danger" data-component="attachment-delete">${escapeHtml(t('attachments.delete'))}</button>
    </div>
  `;
  const openBtn = li.querySelector<HTMLButtonElement>('[data-component="attachment-open"]');
  const delBtn = li.querySelector<HTMLButtonElement>('[data-component="attachment-delete"]')!;
  openBtn?.addEventListener('click', () => onDownload(a, openBtn));
  delBtn.addEventListener('click', () => onDelete(a, delBtn));
  return li;
}

function iconFor(mime: string): string {
  if (mime.startsWith('image/')) return '🖼️';
  if (mime === 'application/pdf') return '📄';
  if (mime.startsWith('text/') || mime === 'application/json') return '📝';
  return '📎';
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
}
